import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import { showLoading } from "react-global-loading";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import { deleteQuestion, getQuestionlists } from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import QuestionDetail from "./QuestionDetail";
import "../Standards/Standards.scss";
import "./QuestionList.scss";

const statusInfo = (value) =>
  value === 1 || value === true || String(value).toLowerCase() === "active"
    ? { label: "Active", tone: "active" }
    : { label: "Inactive", tone: "archived" };

const QuestionActionMenu = ({ question, onEdit, onDelete }) => (
  <ActionMenu
    label={question.question || "question"}
    actions={[
      {
        label: "Edit question",
        icon: <i className="bx bx-pencil" aria-hidden="true" />,
        onSelect: () => onEdit(question.id),
      },
      {
        label: "Delete question",
        icon: <i className="bx bx-trash" aria-hidden="true" />,
        className: "is-danger",
        onSelect: () => onDelete(question),
      },
    ]}
  />
);

const QuestionList = ({ detailId, checklist, closeAction }) => {
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [area, setArea] = useState("all");
  const [status, setStatus] = useState("all");
  const [selectedId, setSelectedId] = useState(0);
  const [formKey, setFormKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [questionPendingDelete, setQuestionPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = () => {
    showLoading(true);
    getQuestionlists(detailId)
      .then((response) => setQuestions(response?.data || []))
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load checklist questions.");
        setQuestions([]);
      })
      .finally(() => showLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [detailId]);

  const openDetail = (id = 0) => {
    setSelectedId(id);
    setFormKey((value) => value + 1);
    setDrawerOpen(true);
  };

  const closeDetail = () => {
    setDrawerOpen(false);
    setSelectedId(0);
    loadData();
  };

  const closeDeleteConfirmation = () => {
    if (!isDeleting) setQuestionPendingDelete(null);
  };

  const confirmDelete = () => {
    if (!questionPendingDelete) return;

    setIsDeleting(true);
    deleteQuestion(questionPendingDelete.id)
      .then((response) => {
        if (!response?.status)
          throw new Error(
            response?.message || "The question could not be deleted."
          );
        setQuestionPendingDelete(null);
        SuccessNotify(response?.message || "Question deleted successfully.");
        loadData();
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify(
          error.message || "Error occurred while deleting the question."
        );
      })
      .finally(() => setIsDeleting(false));
  };
  const areaOptions = useMemo(
    () =>
      [
        ...new Set(
          questions.map((item) => item.checklistArea?.name).filter(Boolean)
        ),
      ].sort(),
    [questions]
  );
  const filteredQuestions = useMemo(
    () =>
      questions.filter((item) => {
        const text = [
          item.question,
          item.referenceNo,
          item.checklistArea?.name,
          item.checklistTopic?.name,
          item.serviceCode?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const itemStatus = statusInfo(item.status).label.toLowerCase();
        return (
          text.includes(search.toLowerCase()) &&
          (area === "all" || item.checklistArea?.name === area) &&
          (status === "all" || itemStatus === status)
        );
      }),
    [area, questions, search, status]
  );

  const columns = [
    {
      id: "question",
      header: "Question",
      accessorKey: "question",
      minSize: 500,
      flex: 2.5,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="checklist-questions__name"
          onClick={() => openDetail(row.original.id)}
        >
          {getValue() || "Untitled question"}
        </button>
      ),
    },
    {
      id: "referenceNo",
      header: "Reference",
      accessorKey: "referenceNo",
      minSize: 125,
      flex: 0.65,
    },
    {
      id: "area",
      header: "Area",
      accessorFn: (item) => item.checklistArea?.name || "—",
      minSize: 130,
      flex: 0.75,
    },
    {
      id: "topic",
      header: "Topic",
      accessorFn: (item) => item.checklistTopic?.name || "—",
      minSize: 130,
      flex: 0.75,
    },
    {
      id: "serviceCode",
      header: "Service Code",
      accessorFn: (item) => item.serviceCode?.name || "—",
      minSize: 140,
      flex: 0.8,
    },
    {
      id: "mustServiceCode",
      header: "Must Service",
      accessorFn: (item) =>
        item.mandatoryServiceCode?.name ||
        item.mandatoryServiceCode?.code ||
        "—",
      minSize: 140,
      flex: 0.8,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (item) => statusInfo(item.status).label,
      minSize: 110,
      flex: 0.55,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 82,
      maxSize: 82,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <QuestionActionMenu
          question={row.original}
          onEdit={openDetail}
          onDelete={setQuestionPendingDelete}
        />
      ),
    },
  ];

  return (
    <section className="checklist-questions">
      <header className="checklist-questions__header">
        <div>
          <span>Checklist questions</span>
          <h1 className="piers-page-title">{checklist?.name || "Checklist Questions"}</h1>
          <p>
            Build and maintain the questions used in this inspection checklist.
          </p>
        </div>
        <button type="button" onClick={() => openDetail()}>
          <i className="bx bx-plus" /> New Question
        </button>
      </header>
      
      <div className="checklist-questions__grid">
        <CustomDataGrid
 filterContent={<section
        className="checklist-questions__filter"
        aria-label="Question filters"
      >
        <div className="checklist-questions__filter-fields">
          <label>
            <span>Search</span>
            <div>
              <i className="bx bx-search" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search questions..."
              />
            </div>
          </label>
          <label>
            <span>Area</span>
            <select
              value={area}
              onChange={(event) => setArea(event.target.value)}
            >
              <option value="all">All Areas</option>
              {areaOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            setArea("all");
            setStatus("all");
          }}
        >Clear</button>
      </section>}

          data={filteredQuestions}
          columns={columns}
          onRefresh={loadData}
          fileName="checklist-questions"
          recordLabel="questions"
          showToolbar={false}
          initialPageSize={-1}
          rowHeight={56}
          columnHeaderHeight={52}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".15px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              borderBottomColor: "#edf0f5",
              color: "#273957",
              fontSize: 13,
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { borderTopColor: "#e7ebf2", fontSize: 14 },
          }}
        />
      </div>
      <Offcanvas
        backdrop="static"
        className="standards-drawer checklist-question-drawer"
        direction="end"
        isOpen={drawerOpen}
        toggle={() => setDrawerOpen(false)}
      >
        <OffcanvasHeader
          toggle={() => setDrawerOpen(false)}
          className="canvasHeader"
        >
          {selectedId ? "Edit Question" : "New Question"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <QuestionDetail
            key={formKey}
            checklistId={detailId}
            detailId={selectedId}
            closeAction={closeDetail}
            onSaved={loadData}
          />
        </OffcanvasBody>
      </Offcanvas>
      <Modal
        isOpen={Boolean(questionPendingDelete)}
        toggle={closeDeleteConfirmation}
        centered
      >
        <ModalBody>
          <h5 className="mb-2">Delete question?</h5>
          <p className="mb-0">
            Delete{" "}
            <strong>
              {questionPendingDelete?.question || "this question"}
            </strong>
            ? This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={closeDeleteConfirmation}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete question"}
          </Button>
        </ModalFooter>
      </Modal>
    </section>
  );
};

export default QuestionList;
